import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Ellipsis, Inbox, MailOpen, RefreshCcw, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { trpc } from "@/components/QueryProvider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn, promiseSleep } from "@/lib/utils";
import { useMarkAllNotificationsAsRead, useRemoveAllNotifications } from "@/mutations/notification-mutations";
import { AlertDialogType } from "@/types/alert-dialog";
import { inboxRoute } from "@/routes/inboxRoute";
import { type AlertDialogImperativeHandle, DefaultAlertDialog } from "../DefaultAlertDialog";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../ui/empty";
import InboxItem from "./InboxItem";
import { getNotificationSearchParams } from "./notification-search-params";

export default function InboxSidebar() {
    const markAllAsReadAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const removeAllAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const { data: notifications, isSuccess, refetch } = useSuspenseQuery(trpc.notification.list.queryOptions());
    const firstLoadDoneRef = useRef(false);
    const { mutateAsync: markAllAsRead } = useMarkAllNotificationsAsRead();
    const { mutateAsync: removeAll } = useRemoveAllNotifications();
    const navigate = useNavigate();
    const { notification: selectedNotificationId } = useSearch({
        from: inboxRoute.id,
        select: (s) => ({ notification: s.notification }),
    });

    const navigateToNotification = (index: number) => {
        const target = notifications[index];
        if (!target) return;
        navigate({
            to: inboxRoute.to,
            search: (prev) => ({ ...prev, ...getNotificationSearchParams(target) }),
        });
    };

    useHotkeys("up", () => {
        const currentIndex = notifications.findIndex((n) => n.id === selectedNotificationId);
        navigateToNotification(currentIndex <= 0 ? 0 : currentIndex - 1);
    });

    useHotkeys("down", () => {
        const currentIndex = notifications.findIndex((n) => n.id === selectedNotificationId);
        navigateToNotification(currentIndex === -1 ? 0 : Math.min(currentIndex + 1, notifications.length - 1));
    });

    if (isSuccess) {
        // after the first load AND first display, remember it
        setTimeout(() => {
            firstLoadDoneRef.current = true;
        }, 0);
    }

    const handleMarkAllAsRead = async () => {
        if (await markAllAsReadAlertDialogRef.current?.show()) {
            setIsRefreshing(true);
            await Promise.all([markAllAsRead(), promiseSleep(700)]);
            setIsRefreshing(false);
        }
    };

    const handleRemoveAll = async () => {
        if (await removeAllAlertDialogRef.current?.show()) {
            setIsRefreshing(true);
            await Promise.all([removeAll(), promiseSleep(700)]);
            setIsRefreshing(false);
        }
    };

    const handleRefresh = async () => {
        // Make sure this takes at least a second, to show the indicator
        setIsRefreshing(true);
        await Promise.all([refetch(), promiseSleep(1000)]);
        setIsRefreshing(false);
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between py-2 pl-2 pr-3 border-b border-neutral-200">
                <div className="font-medium  flex items-center">
                    <span className="size-9 grid place-items-center">
                        <Inbox className="size-4" />
                    </span>
                    <div className="ml-2 text-zinc-600">Inbox</div>
                </div>
                <div>
                    <Button onClick={handleRefresh} variant="ghost" size="icon" className="ml-auto">
                        <RefreshCcw className={cn("size-4", isRefreshing && "animate-spin")} />
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            render={
                                <Button variant="ghost" size="icon" className="ml-auto">
                                    <Ellipsis className="size-4" />
                                </Button>
                            }
                        ></DropdownMenuTrigger>
                        <DropdownMenuContent side="bottom" align="end">
                            <DropdownMenuItem onClick={handleMarkAllAsRead}>
                                <MailOpen />
                                Mark all as read
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleRemoveAll}>
                                <Trash2 />
                                Remove all from inbox
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            <div className="flex-1 relative">
                <div className="absolute inset-0">
                    <ScrollArea className="h-full w-full">
                        {/* {newNotificationsVisible && <NewNotifications handleRefresh={handleRefresh} isFetching={isFetching} />} */}
                        <div className="flex flex-col gap-0.5 py-1">
                            {notifications.map((notification, index) => (
                                <InboxItem
                                    showAnimation={firstLoadDoneRef.current}
                                    notification={notification}
                                    nextNotification={notifications[index + 1]}
                                    key={notification.id}
                                />
                            ))}
                        </div>
                    </ScrollArea>
                </div>
                {notifications.length === 0 && (
                    <Empty className=" h-full w-full">
                        <EmptyHeader>
                            <EmptyMedia variant="icon">
                                <Inbox />
                            </EmptyMedia>
                            <EmptyTitle>No notifications</EmptyTitle>
                            <EmptyDescription>There are no notifications in your inbox.</EmptyDescription>
                        </EmptyHeader>
                    </Empty>
                )}
            </div>
            <DefaultAlertDialog
                ref={markAllAsReadAlertDialogRef}
                type={AlertDialogType.Confirm}
                title="Mark all as read"
                description="Are you sure you want to mark all notifications as read?"
            />
            <DefaultAlertDialog
                ref={removeAllAlertDialogRef}
                type={AlertDialogType.Confirm}
                title="Remove all notifications"
                description="Are you sure you want to remove all notifications from your inbox?"
            />
        </div>
    );
}
